import {
  ChevronDown,
  ChevronUp,
  Clock,
  MapPin,
  Users,
  Calendar,
} from 'lucide-react';
import { OohPoint } from '../../../../core/graphql/types';
import { useState } from 'react';
import useOohVisitsDataStore from '../../../../core/hooks/useOohVisitsDataStore';
import useUrl from '../../../../core/hooks/useUrl';
import { format, parseISO } from 'date-fns';
import {
  sortSchedules,
  sortSpecialSchedules,
  formatScheduleTime,
  formatSpecialScheduleDate,
  getDayName,
} from '../../../../core/utils/ooh/scheduleUtils';
import { getActivityBars } from '../../../../core/utils/ooh/oohCalculations';
import ActivityBar from '../content/ActivityBar';
import LoadingSpinner from '../../../../components/loadingSpinner/LoadingSpinner';

export interface OohPopupContentProps {
  oohPoint: OohPoint;
}

const OohPopupContent = ({ oohPoint }: OohPopupContentProps) => {
  const [isScheduleExpanded, setIsScheduleExpanded] = useState(false);
  const { paramValue: dateParamValue } = useUrl<string>('Date');
  const { id, name, oohType, capacity, schedules, specialSchedules } = oohPoint;
  const { oohVisits, areOohVisitsLoading } = useOohVisitsDataStore(dateParamValue, id);

  const sortedSchedules = sortSchedules(schedules!);
  const sortedSpecialSchedules = sortSpecialSchedules(specialSchedules!);

  const renderSchedules = () => {
    return (
      <div className="mb-4">
        <button
          onClick={() => setIsScheduleExpanded(!isScheduleExpanded)}
          className="flex w-full items-center justify-between rounded-sm p-2 transition-colors duration-200 hover:bg-light-gray"
        >
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-design-gray" />
            <span className="text-sm font-medium text-design-gray">
              Schedule
            </span>
          </div>
          {isScheduleExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {isScheduleExpanded && (
          <div className="mt-2 space-y-2">
            <div className="space-y-1">
              {sortedSchedules.map((schedule) => (
                <div
                  key={schedule.id}
                  className="flex items-center justify-between text-xs"
                >
                  <span className="w-8 font-medium">
                    {getDayName(schedule.dayOfWeek)}
                  </span>
                  <span>
                    {formatScheduleTime(
                      schedule.openingTime,
                      schedule.closingTime,
                    )}
                  </span>
                </div>
              ))}
            </div>

            {sortedSpecialSchedules.length > 0 && (
              <div className="border-gray-200 dark:border-gray-600 space-y-1 border-t pt-2">
                {sortedSpecialSchedules.map((special) => (
                  <div
                    key={special.id}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="text-gray-600 dark:text-gray-400 font-medium">
                      {formatSpecialScheduleDate(
                        special.dayOfMonth,
                        special.month,
                      )}
                    </span>
                    <span className="text-gray-800 dark:text-gray-200">
                      {special.bankHoliday
                        ? 'Closed'
                        : special.openingTime && special.closingTime
                          ? `${special.openingTime}–${special.closingTime}`
                          : 'Closed'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderOohVisits = () => {
    return (
      <div>
        <div className="mb-3 flex items-center gap-2 p-2">
          <Calendar className="h-4 w-4" />
          <span className="text-sm font-medium">
            Recent Visits {!areOohVisitsLoading && `(${oohVisits?.length || 0})`}
          </span>
        </div>

        <div className="max-h-[300px] space-y-3 overflow-y-auto">
          {areOohVisitsLoading ? (
            <LoadingSpinner />
          ) : oohVisits?.length > 0 ? (
            oohVisits.map((visit, index) => (
              <div key={index} className="rounded-lg bg-light-gray p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {visit.courierIndicative}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400 text-xs">
                    {`${
                      visit.visitStartTime
                        ? format(parseISO(visit.visitStartTime), 'HH:mm')
                        : 'Unknown'
                    } - ${
                      visit.visitEndTime
                        ? format(parseISO(visit.visitEndTime), 'HH:mm')
                        : 'Unknown'
                    }`}
                  </span>
                </div>

                <ActivityBar activities={getActivityBars(visit)} />
              </div>
            ))
          ) : (
            <div className="py-4 text-center">
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                No visits found for the selected date
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-w-[300px] max-w-[400px] rounded-sm p-4">
      <div className="mb-4">
        <div className="mb-2 flex items-start gap-3">
          <div className="flex-shrink-0 rounded-sm bg-bright-blue/20 p-2">
            <MapPin className="h-5 w-5 text-bright-blue" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-lg font-bold ">{name}</h3>
            <p className="text-sm text-design-gray">{oohType}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-2">
          <Users className="h-4 w-4" />
          <span className="text-sm font-medium text-design-gray">
            Capacity:{' '}
            <span className="font-bold text-bright-blue">{capacity}</span>
          </span>
        </div>
      </div>
      {renderSchedules()}
      {renderOohVisits()}
    </div>
  );
};

export default OohPopupContent;
